// ✅ src/pages/VehicleV2/VehicleTransferReport.jsx
// UPDATED:
// - gets finYearId, finYear, branchId, branchType, userId from localStorage
// - sends backend-matching headers in all axios calls
// - keeps same UI and API flow

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "http://nissi-india.com/api/vehicleTransferReport";

/* ---------- helpers ---------- */
const toStr = (v) => String(v ?? "").trim();

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select" || s === "0";
};

const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const downloadCsv = (rows, filename = "vehicle-transfer-report.csv") => {
  if (!rows?.length) return toast.info("No rows to export");

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const val = r?.[h] ?? "";
          const s = String(val).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function VehicleTransferReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ backend-matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  // radio type: Branch / Location
  const [type, setType] = useState("B");

  const [branches, setBranches] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    fromDate: daysAgoISO(30),
    toDate: todayISO(),
    vehicleName: "",
    vehicleRegistrationNumber: "",
    vehicleCode: "",
    fromBranchId:
      sessionBranchType === "HO" ? "0" : String(sessionBranchId || "0"),
    toBranchId: "0",
    vehicleMainTypeId: "0",
  });

  const [rows, setRows] = useState([]);

  /* ---------- styles ---------- */
  const inputClass = useMemo(() => {
    return `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
      isDark
        ? "bg-gray-700/30 border-gray-600 text-white"
        : "bg-white border-gray-300 text-gray-900"
    }`;
  }, [isDark]);

  const labelClass = useMemo(() => {
    return `block text-sm font-medium mb-2 ${
      isDark ? "text-gray-300" : "text-gray-700"
    }`;
  }, [isDark]);

  /* ---------- load meta ---------- */
  const loadMeta = async () => {
    try {
      const [b, vt] = await Promise.all([
        axios.get(`${API_BASE}/meta/branches`, { headers: apiHeaders }),
        axios.get(`${API_BASE}/meta/vehicleMainTypes`, { headers: apiHeaders }),
      ]);

      setBranches(b?.data?.rows || []);
      setVehicleTypes(vt?.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Meta load failed");
    }
  };

  /* ---------- load list ---------- */
  const loadList = async (p = filters, t = type) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        fromDate: toStr(p.fromDate),
        toDate: toStr(p.toDate),
        vehicleName: toStr(p.vehicleName),
        vehicleRegistrationNumber: toStr(p.vehicleRegistrationNumber),
        vehicleCode: toStr(p.vehicleCode),
        fromBranchId: isSelect(p.fromBranchId) ? "0" : toStr(p.fromBranchId),
        toBranchId: isSelect(p.toBranchId) ? "0" : toStr(p.toBranchId),
        vehicleMainTypeId: isSelect(p.vehicleMainTypeId)
          ? "0"
          : toStr(p.vehicleMainTypeId),
        type: t,
      });

      const rs = await axios.get(`${API_BASE}/list?${params.toString()}`, {
        headers: apiHeaders,
      });

      const list = rs?.data?.rows || [];
      setRows(list);
      toast.success(`Found ${list.length} record(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "List load failed");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- change handlers ---------- */
  const handleChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSearch = () => loadList(filters, type);

  const handleViewAll = () => {
    const reset = {
      fromDate: "",
      toDate: "",
      vehicleName: "",
      vehicleRegistrationNumber: "",
      vehicleCode: "",
      fromBranchId:
        sessionBranchType === "HO" ? "0" : String(sessionBranchId || "0"),
      toBranchId: "0",
      vehicleMainTypeId: "0",
    };
    setFilters(reset);
    loadList(reset, type);
  };

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      fromDate: "",
      toDate: "",
      vehicleName: "",
      vehicleRegistrationNumber: "",
      vehicleCode: "",
      toBranchId: "0",
      vehicleMainTypeId: "0",
      fromBranchId:
        sessionBranchType === "HO"
          ? prev.fromBranchId
          : String(sessionBranchId || "0"),
    }));
    toast.info("Filters cleared");
  };

  const handleExport = () => {
    if (!rows?.length) return toast.info("No data to export");

    const exportRows = rows.map((r) => ({
      sno: r.sno,
      vehicleMainType: r.vehicleMainType,
      vehicleCode: r.vehicleCode,
      vehicleRegNo: r.vehicleRegNo,
      transferFrom: r.transferFrom,
      transferTo: r.transferTo,
      fbRecDate: r.fbRecDate,
      transferDate: r.transferDate,
      transferBy: r.transferBy,
      approvedBy: r.approvedBy,
      approvedDate: r.approvedDate,
      workHr: r.workHr,
      cumHr: r.cumHr,
    }));

    downloadCsv(exportRows);
    toast.success("Exported CSV");
  };

  const handlePrintAll = () => {
    const qs = new URLSearchParams({
      ...filters,
      type,
    }).toString();

    navigate(`/vehicle/VehicleTransferReport_Print?${qs}`);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TRANSFER REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Transfer Report
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {rows.length} Records
              </span>
              {loading && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="flex items-center gap-6 mb-6">
                <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  <input
                    type="radio"
                    name="trType"
                    value="B"
                    checked={type === "B"}
                    onChange={() => setType("B")}
                  />
                  Branch
                </label>
                <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  <input
                    type="radio"
                    name="trType"
                    value="L"
                    checked={type === "L"}
                    onChange={() => setType("L")}
                  />
                  Location
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Vehicle Name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input
                    type="text"
                    name="vehicleRegistrationNumber"
                    value={filters.vehicleRegistrationNumber}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Vehicle Reg No"
                  />
                </div>

                <div>
                  <label className={labelClass}>From Branch *</label>
                  <select
                    name="fromBranchId"
                    value={filters.fromBranchId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={sessionBranchType !== "HO"}
                  >
                    <option value="0">Select</option>
                    {branches.map((b) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Branch Name *</label>
                  <select
                    name="toBranchId"
                    value={filters.toBranchId}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="0">Select</option>
                    {branches.map((b) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main type</label>
                  <select
                    name="vehicleMainTypeId"
                    value={filters.vehicleMainTypeId}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="0">Select</option>
                    {vehicleTypes.map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        {t.name || t.code || t.id}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Vehicle Code"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 gap-3">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                  >
                    <FaSearch />
                    Search
                  </button>

                  <button
                    onClick={handlePrintAll}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                  >
                    <FaPrint />
                    Print
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Transfer Records Found
                </h3>
                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try Search or View All.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-3 py-3 text-center">S.No</th>
                      <th className="px-3 py-3 text-center">Vehicle Main Type</th>
                      <th className="px-3 py-3 text-center">Vehicle Code</th>
                      <th className="px-3 py-3 text-center">Vehicle RegNo</th>
                      <th className="px-3 py-3 text-center">Transfer From</th>
                      <th className="px-3 py-3 text-center">Transfer To</th>
                      <th className="px-3 py-3 text-center">FB Rec Date</th>
                      <th className="px-3 py-3 text-center">Transfer Date</th>
                      <th className="px-3 py-3 text-center">Transfer By</th>
                      <th className="px-3 py-3 text-center">Approved By</th>
                      <th className="px-3 py-3 text-center">Approved Date</th>
                      <th className="px-3 py-3 text-center">Work HR</th>
                      <th className="px-3 py-3 text-center">CUM HR</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, i) => (
                      <tr
                        key={`${r.transferRowId || i}`}
                        className={`${isDark ? "bg-gray-800" : "bg-white"} hover:${
                          isDark ? "bg-gray-700" : "bg-gray-50"
                        } transition-colors duration-200`}
                      >
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.sno ?? i + 1}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.vehicleMainType || ""}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.vehicleCode || ""}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.vehicleRegNo || ""}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.transferFrom || ""}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.transferTo || ""}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.fbRecDate || ""}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.transferDate || ""}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.transferBy || ""}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.approvedBy || ""}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.approvedDate || ""}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {Number(r.workHr ?? 0).toFixed(2)}
                        </td>
                        <td className={`px-3 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {Number(r.cumHr ?? 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}